// this is a page for marketing/sales purposes
// users can select what type of document they want to OCR
// and we give them back the correction screen along with the data

import { Publish } from '@mui/icons-material'
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	MenuItem,
	Select,
	SelectChangeEvent,
	Typography,
} from '@mui/material'
import { useCallback, useState } from 'react'
import Dropzone, { DropzoneState } from 'react-dropzone'
import {
	SignalTypeIds,
	WorkflowTaskEventAction,
	WorkflowTaskWorkflowEventData,
} from '../../api/clients/workflows/DTOs'
import TagsClient from '../../api/clients/workflows/TagsClient'
import WorkflowsClient from '../../api/clients/workflows/WorkflowsClient'
import { OcrCorrectionPageWrapper } from '../../modules/Ocr/OcrCorrectionPageWrapper'
import { usePrompts } from '../../services/notifications/PromptService'
import { uploadBlob } from '../../utils/BlobUtils'
import { CompletedDemoDialog } from './CompletedDemoDialog'

export const OcrDemoPage = () => {
	const [workflowTaskId, setWorkflowTaskId] = useState<number>()
	const [dialogOpen, setDialogOpen] = useState(true)
	const [isRunningWorkflow, setIsRunningWorkflow] = useState(false)
	const [isSubmitting, setIsSubmitting] = useState(false)

	const promptService = usePrompts()

	const handleRunOcr = useCallback(
		async (documentType: string, blobId: number) => {
			setIsRunningWorkflow(true)

			const workflowsClient = new WorkflowsClient()
			const workflowTagsClient = new TagsClient()

			const { data: workflowDefinitions } =
				await workflowTagsClient.queryWorkflows([
					{
						class:
							'KeyMarkLabs.BlobStorage.Abstractions.Models.BlobRecord.Name',
						value: 'ocr-demo',
					},
				])

			const workflowDefinition = workflowDefinitions[0]

			const { data: startedWorkflowModel } =
				await workflowsClient.createWorkflowSignal(
					workflowDefinition.id,
					SignalTypeIds.DocumentUploaded,
					[
						{ type: 'DocumentType', value: documentType },
						{ type: 'Blob', value: blobId },
					],
				)

			const interval = setInterval(async () => {
				const { data: status } = await workflowsClient.getWorkflowStatus(
					startedWorkflowModel.workflowInstanceId,
				)

				const workflowTasks = status.workflowEvents
					.filter((v) => v.eventType === 'WorkflowTask')
					.map((v) => v.eventData as WorkflowTaskWorkflowEventData)

				if (workflowTasks.length > 0) {
					console.log('clearing interval')
					window.clearInterval(interval)
				}

				const workflowTask = workflowTasks[0]

				if (workflowTask.action === WorkflowTaskEventAction.Created) {
					setIsRunningWorkflow(false)
					setWorkflowTaskId(workflowTask.workflowTaskId)
				}
			}, 5000)
		},
		[],
	)

	if (workflowTaskId !== undefined)
		return (
			<OcrCorrectionPageWrapper
				onDocumentReviewed={(outcome, comparedValues) => {
					promptService
						.showDialog((props) => (
							<CompletedDemoDialog
								onConfirm={() => props.close('okay')}
								onClose={() => props.close('cancel')}
							/>
						))
						.then(async (v) => {
							if (v === 'okay') setWorkflowTaskId(undefined)
						})
				}}
				isSubmitting={isSubmitting}
				workflowTaskId={workflowTaskId}
			/>
		)

	return (
		<Box>
			<DocumentUploadDialog
				open={dialogOpen}
				onRunOcr={handleRunOcr}
				isLoading={isRunningWorkflow}
			/>
		</Box>
	)
}

type DocumentUploadDialogProps = {
	open: boolean
	onRunOcr: (documentType: string, blobId: number) => void
	isLoading: boolean
}

const DocumentUploadDialog = ({
	open,
	onRunOcr,
	isLoading,
}: DocumentUploadDialogProps) => {
	const [documentType, setDocumentType] = useState<string>()
	const [file, setFile] = useState<File>()
	const [isUploading, setIsUploading] = useState(false)

	const fileReader = new FileReader()

	const handleDrop = useCallback((acceptedFiles: File[]) => {
		const droppedFile = acceptedFiles[0]

		try {
			fileReader.readAsArrayBuffer(droppedFile as Blob)
		} catch (e) {
			console.error(e)

			throw new Error('error reading file as array buffer')
		}

		setFile(droppedFile)
	}, [])

	const handleUpload = async () => {
		if (file === undefined || documentType === undefined) return

		setIsUploading(true)

		// upload the blob (in chunks if necessary)
		const blobId = await uploadBlob(fileReader, file, 'OcrDemoBlob').finally(
			() => setIsUploading(false),
		)

		if (blobId === undefined) throw new Error('error uploading blob')

		onRunOcr(documentType, blobId)
	}

	return (
		<Dialog open={open} maxWidth="sm" fullWidth>
			<DialogTitle align="center">Forms inMotion OCR Demo</DialogTitle>
			<DialogContent>
				<Box display="flex" flexDirection="column" gap={2}>
					<Typography variant="body1">
						Test out Forms inMotion's optical character recognition capabilties
						by uploading a document and selecting its type.
					</Typography>
					<Box
						display="flex"
						flexDirection="row"
						justifyContent="center"
						alignItems="center"
						gap={2}
					>
						<Typography variant="subtitle1">Select a document type:</Typography>
						<Box
							component={Select}
							size="small"
							width="30%"
							value={documentType}
							onChange={(evt: SelectChangeEvent<unknown>) =>
								setDocumentType(evt.target.value as string)
							}
							disabled={isUploading || isLoading}
						>
							<MenuItem value="check" key="check">
								Check
							</MenuItem>
							<MenuItem value="invoice" key="invoice">
								Invoice
							</MenuItem>
						</Box>
					</Box>
					<Dropzone
						maxFiles={1}
						maxSize={30000000}
						accept={'image/tiff'}
						multiple={false}
						onDrop={handleDrop}
						disabled={isUploading || isLoading}
					>
						{({ getRootProps, getInputProps }: DropzoneState) => (
							<Box marginY={2} display="flex" justifyContent="center">
								<div {...getRootProps()}>
									<input {...getInputProps()} />
									<Box
										display="flex"
										flexDirection="column"
										justifyContent="center"
										alignItems="center"
										borderRadius={4}
										border="dashed"
										borderColor="text.secondary"
										sx={{
											cursor: isLoading ? 'default' : 'pointer',
										}}
										minWidth="500px"
										minHeight="200px"
									>
										{isUploading || isLoading ? (
											<CircularProgress />
										) : file !== undefined ? (
											<Typography>{file.name}</Typography>
										) : (
											<>
												<Typography>
													Drag and drop your file here, or click to select a
													file
												</Typography>
												<Publish />
											</>
										)}
									</Box>
								</div>
							</Box>
						)}
					</Dropzone>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button
					variant="contained"
					disabled={
						file === undefined ||
						documentType === undefined ||
						isUploading ||
						isLoading
					}
					onClick={handleUpload}
				>
					Run OCR
				</Button>
			</DialogActions>
		</Dialog>
	)
}
